'use client';

import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/utils';

interface Effect {
  id: string;
  name: string;
  image: string;
  description: string;
}

const EFFECTS: Effect[] = [
  {
    id: 'cinematic',
    name: 'Cinematic Enhancement',
    image: 'https://r2.zestygen.com/effects/10.webp',
    description: 'Transform photos into cinematic-quality visuals with professional color grading',
  },
  {
    id: 'detail-boost',
    name: 'Detail Boost',
    image: 'https://r2.zestygen.com/effects/20.webp',
    description: 'Enhance clarity, textures, and fine details for stunning results',
  },
  {
    id: 'realistic',
    name: 'Realistic Upgrade',
    image: 'https://r2.zestygen.com/effects/30.webp',
    description: 'Subtle improvements while preserving natural realism',
  },
  {
    id: 'artistic',
    name: 'Artistic Style',
    image: 'https://r2.zestygen.com/effects/40.webp',
    description: 'Apply artistic flair while keeping the original structure',
  },
  {
    id: 'creative',
    name: 'Creative Reimagine',
    image: 'https://r2.zestygen.com/effects/50.webp',
    description: 'Bold transformations with stronger creative interpretation',
  },
];

export function EffectsShowcase() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % EFFECTS.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + EFFECTS.length) % EFFECTS.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % EFFECTS.length);
  };

  const goToSlide = (index: number) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  const currentEffect = EFFECTS[currentIndex];

  return (
    <section className="py-16 md:py-24 bg-muted/30">
      <div className="container">
        <div className="mx-auto max-w-6xl">
          {/* Header */}
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
              Transform Your Photos with AI
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Explore our powerful AI effects and see the magic in action
            </p>
          </div>

          {/* Carousel */}
          <div className="relative">
            {/* Main Image Display */}
            <div className="relative overflow-hidden rounded-2xl border-2 border-border bg-background shadow-2xl">
              <div className="aspect-[16/9] relative">
                <img
                  src={currentEffect.image}
                  alt={currentEffect.name}
                  className="h-full w-full object-cover transition-opacity duration-500"
                />

                {/* Gradient Overlay for Text */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                {/* Effect Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <div className="space-y-2">
                    <h3 className="text-2xl font-bold text-white md:text-3xl">
                      {currentEffect.name}
                    </h3>
                    <p className="text-sm text-white/90 md:text-base">
                      {currentEffect.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Navigation Arrows */}
              <Button
                variant="secondary"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full shadow-lg"
                onClick={goToPrevious}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full shadow-lg"
                onClick={goToNext}
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>

            {/* Dots Indicator */}
            <div className="mt-6 flex items-center justify-center gap-2">
              {EFFECTS.map((effect, index) => (
                <button
                  key={effect.id}
                  onClick={() => goToSlide(index)}
                  className={cn(
                    'h-2 rounded-full transition-all duration-300',
                    index === currentIndex
                      ? 'w-8 bg-primary'
                      : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  )}
                  aria-label={`Go to ${effect.name}`}
                />
              ))}
            </div>

            {/* Thumbnail Navigation */}
            <div className="mt-8 grid grid-cols-5 gap-3">
              {EFFECTS.map((effect, index) => (
                <button
                  key={effect.id}
                  onClick={() => goToSlide(index)}
                  className={cn(
                    'group relative overflow-hidden rounded-lg border-2 transition-all duration-300',
                    index === currentIndex
                      ? 'border-primary ring-2 ring-primary ring-offset-2 ring-offset-background'
                      : 'border-transparent hover:border-muted-foreground/30'
                  )}
                >
                  <div className="aspect-video relative">
                    <img
                      src={effect.image}
                      alt={effect.name}
                      className={cn(
                        'h-full w-full object-cover transition-all duration-300',
                        index === currentIndex
                          ? 'scale-100 opacity-100'
                          : 'scale-95 opacity-60 group-hover:scale-100 group-hover:opacity-80'
                      )}
                    />
                    {index === currentIndex && (
                      <div className="absolute inset-0 bg-primary/10" />
                    )}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <p className="text-xs font-medium text-white truncate">
                      {effect.name}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
